import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MdClose } from "react-icons/md";

import { fetchTransferDestinations, requestTransfer } from "../notifications/api";
import "../notifications.css";

/* ------------------------------------------------------------------ *
 * TransferOrderModal — "send this work order to another business".
 *
 * The source half of the transfer flow (docs §4.1 + §4.2): pick a
 * destination from GET /work-orders/transfers/destinations, add an
 * optional note, POST /request. Nothing moves here — the order stays
 * fully usable in this business until the destination accepts.
 *
 * The one failure worth its own treatment is 409: a request for this
 * order is already awaiting approval. `details` names the business and
 * carries the transfer id, so the modal offers to open it instead of
 * repeating the refusal.
 *
 * Props
 *   order      { id, serial, type, name }
 *   onClose()
 *   onRequested(transfer)   the new pending transfer
 * ------------------------------------------------------------------ */

const businessLine = (b) =>
  `${b.name || "Business"}${b.code ? ` (${b.code})` : ""}`;

export function TransferOrderModal({ order, onClose, onRequested }) {
  const navigate = useNavigate();

  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [toId, setToId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  /* The 409 case: { transfer_id, to_business } of the request already out. */
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchTransferDestinations()
      .then((list) => {
        if (cancelled) return;
        const rows = Array.isArray(list) ? list : [];
        setDestinations(rows);
        if (rows.length === 1) setToId(rows[0].id);
      })
      .catch((e) => {
        if (cancelled) return;
        setDestinations([]);
        setLoadError(
          (e && e.response && e.response.data && e.response.data.message) ||
            "Could not load the list of businesses."
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const chosen = destinations.find((d) => d.id === toId) || null;

  function submit() {
    if (!toId || busy) return;
    setBusy(true);
    setError("");
    setExisting(null);
    requestTransfer({
      work_order_id: order.id,
      to_business_id: toId,
      note: note.trim(),
    })
      .then((transfer) => onRequested && onRequested(transfer))
      .catch((e) => {
        const res = (e && e.response) || {};
        const body = res.data || {};
        if (res.status === 409 && body.details && body.details.transfer_id) {
          setExisting(body.details);
          return;
        }
        setError(body.message || "Could not send the request.");
      })
      .finally(() => setBusy(false));
  }

  return (
    <div className="tx-wrap" role="dialog" aria-label="Transfer work order">
      <div className="tx-scrim" onClick={onClose} />
      <div className="tx-modal">
        <header className="tx-head">
          <div>
            <div className="tx-eyebrow">Transfer work order</div>
            <h2 className="rf-serif tx-title">{order.serial}</h2>
            <p className="tx-sub">
              {order.type ? `${order.type} · ` : ""}
              {order.name || "—"}
            </p>
          </div>
          <button type="button" className="rf-icon-btn" onClick={onClose} aria-label="Close">
            <MdClose />
          </button>
        </header>

        <div className="tx-body">
          {existing ? (
            <div className="tx-exists">
              <b>This order is already on its way out.</b>
              <p>
                {businessLine(existing.to_business || {})} has been asked to take it and
                hasn&apos;t decided yet. Cancel that request before sending a new one.
              </p>
              <button
                type="button"
                className="rf-btn rf-btn-primary"
                onClick={() => navigate(`/transfers/${existing.transfer_id}`)}
              >
                Open the pending request
              </button>
            </div>
          ) : (
            <>
              <p className="tr-help tx-lede">
                The order stays here and stays editable until the other business accepts
                it. It keeps its serial number{order.serial ? ` (${order.serial})` : ""}.
              </p>

              <div className="tx-field">
                <label className="rf-label" htmlFor="tx-dest">
                  Send to
                </label>
                {loading ? (
                  <div className="tr-help">Loading businesses…</div>
                ) : loadError ? (
                  <div className="nf-error" style={{ margin: 0 }}>{loadError}</div>
                ) : destinations.length === 0 ? (
                  <div className="tr-help">
                    There is no other active business to send this order to.
                  </div>
                ) : (
                  <div className="tx-choices">
                    {destinations.map((d) => (
                      <label
                        key={d.id}
                        className={`tx-choice${toId === d.id ? " is-on" : ""}`}
                      >
                        <input
                          type="radio"
                          name="tx-dest"
                          value={d.id}
                          checked={toId === d.id}
                          onChange={() => setToId(d.id)}
                          disabled={busy}
                        />
                        <span className="tx-choice-mark" aria-hidden="true">
                          {d.code || (d.name || "?").slice(0, 2).toUpperCase()}
                        </span>
                        <span className="tx-choice-name">{d.name || "Business"}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="tx-field">
                <label className="rf-label" htmlFor="tx-note">
                  Note for them (optional)
                </label>
                <textarea
                  id="tx-note"
                  className="tr-textarea"
                  placeholder="Why this order is going across"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={busy}
                />
              </div>

              {error && <div className="nf-error" style={{ margin: 0 }}>{error}</div>}
            </>
          )}
        </div>

        {!existing && (
          <footer className="tx-foot">
            <button type="button" className="rf-btn rf-btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="rf-btn rf-btn-primary"
              onClick={submit}
              disabled={busy || !toId}
            >
              {busy
                ? "Sending…"
                : chosen
                ? `Send to ${chosen.name || "them"}`
                : "Send request"}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}

export default TransferOrderModal;
